import logoSvg from "./logo.svg?raw";
import cMarkSvg from "./c-mark.svg?raw";
import bhcgLogoUrl from "./centivo-bhcg-logo.png";
import asoHtml from "./aso.html?raw";
import asoCentivoHtml from "./aso-centivo.html?raw";
import asoWisconsinHtml from "./aso-wisconsin.html?raw";
import { sectionHasValues } from "./emptyProposal.js";
import { buildLevelFundedTables } from "./levelFundedTables.js";
import {
    ASO_CIGNA_PPO_COPY,
    ASO_PLAN_OFFERING_COPY,
    ASO_PROPOSED_PBM_NAME_FALLBACK,
    ASO_PROPRIETARY_NETWORK_FALLBACK,
    ASO_STOP_LOSS_ARRANGE_COPY,
    ASO_STOP_LOSS_INCLUDED_COPY,
    ASO_STOP_LOSS_PARTNERS_COPY,
    ASO_WI_CIGNA_PBM_COPY,
    ASO_WI_PROPRIETARY_NETWORK_FALLBACK,
    employeeTerms,
    getAsoPricing,
} from "./asoModel.js";
import { renderMustache } from "./renderMustache.js";
import { contingenciesHtml } from "./contingenciesHtml.js";

function formatProposalDate(value) {
    const raw = String(value || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const [year, month, day] = raw.split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    });
}

const TEMPLATES = {
    aso: asoHtml,
    "aso-centivo": asoCentivoHtml,
    "aso-wisconsin": asoWisconsinHtml,
};

function svgUri(svg) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function absoluteAsset(url) {
    if (!url || typeof window === "undefined") return url || "";
    try {
        return new URL(url, window.location.href).href;
    } catch {
        return url;
    }
}

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

function placeholder(label) {
    return `<p class="tpl-data-placeholder">Drop the underwriting workbook to fill ${escapeHtml(label)}.</p>`;
}

function asoTables(draft) {
    const built = buildLevelFundedTables(draft?.data, draft?.ratesTableVariant);
    const data = draft?.data || {};
    return {
        planDesign: sectionHasValues(data.planDesign) ? built.planDesign : placeholder("plan design"),
        asoSalesPricingGrid: sectionHasValues(data.asoOption) ? built.asoOption : placeholder("the illustrative ASO premium"),
        rates: sectionHasValues(data.rates) || sectionHasValues(data.ratesWithWriteWise) || sectionHasValues(data.lfRateTable)
            ? built.rates
            : placeholder("rate equivalents"),
        networkOverview: placeholder("the network overview"),
    };
}

function footerPages(draft) {
    const wisconsin = draft.templateType === "aso-wisconsin";
    const keys = ["planOffering"];
    if (wisconsin && draft.includeAsoWiNetworkAccess !== false) keys.push("networkAccess");
    keys.push("pricing", "pricingContinued");
    if (wisconsin) keys.push("pricingFees");
    if (draft.includeIllustrativeAsoPremium) keys.push("illustrativeAsoPremium");
    if (draft.includeAsoEquivalents) keys.push("equivalents");
    keys.push("whatYouCanExpect", "appendix", "appendixMembers");
    if (wisconsin) keys.push("appendixStopLoss", "appendixSuccessFee");
    const pages = {};
    keys.forEach((key, index) => {
        pages[key] = index + 2;
    });
    return pages;
}

export function renderAso(draft) {
    const templateType = TEMPLATES[draft?.templateType] ? draft.templateType : "aso";
    const companyName = draft?.companyName?.trim() || "[Company Name]";
    const variant = draft?.planOfferingVariant === "partnership-only" ? "partnership-only" : "partnership-cigna";
    const solution = ["wi-slice", "wi-full-replacement", "wi-both"].includes(draft?.asoWiSolutionVariant)
        ? draft.asoWiSolutionVariant
        : "wi-both";
    const includeAsoWiCigna = draft?.includeAsoWiCigna !== false;
    const asoWiShowBroadNetwork = solution !== "wi-slice";
    const terms = employeeTerms(draft?.asoWiEmployeeTerm);
    const pbm = String(draft?.asoProposedPbmName || "").trim();
    const network = String(draft?.asoProprietaryNetwork || "").trim();
    const stopLoss = contingenciesHtml(draft?.stopLossContingencies);
    return renderMustache(TEMPLATES[templateType], {
        companyName,
        proposalDate: formatProposalDate(draft?.proposalDate) || "Date TBD",
        planOfferingCopy: ASO_PLAN_OFFERING_COPY[variant],
        asoCignaPpoCopy: templateType === "aso" ? ASO_CIGNA_PPO_COPY : "",
        asoOpeningSentence: String(draft?.asoOpeningSentence || "").trim(),
        asoProprietaryNetwork: network || (templateType === "aso-wisconsin" ? ASO_WI_PROPRIETARY_NETWORK_FALLBACK : ASO_PROPRIETARY_NETWORK_FALLBACK),
        asoProposedPbmName: pbm || ASO_PROPOSED_PBM_NAME_FALLBACK,
        showAsoPharmacy: Boolean(pbm),
        asoPricing: getAsoPricing(draft?.asoPricing, templateType),
        asoStopLossArrangeCopy: ASO_STOP_LOSS_ARRANGE_COPY,
        asoStopLossIncludedCopy: ASO_STOP_LOSS_INCLUDED_COPY,
        asoStopLossPartnersCopy: ASO_STOP_LOSS_PARTNERS_COPY,
        asoWiCignaPbmCopy: ASO_WI_CIGNA_PBM_COPY,
        includeIllustrativeAsoPremium: Boolean(draft?.includeIllustrativeAsoPremium),
        includeAsoEquivalents: Boolean(draft?.includeAsoEquivalents),
        includeAsoStopLossQuotes: Boolean(draft?.includeAsoStopLossQuotes),
        includeAsoWiCignaPbm: Boolean(draft?.includeAsoWiCignaPbm),
        includeAsoWiCigna,
        includeAsoWiNetworkAccess: draft?.includeAsoWiNetworkAccess !== false,
        includeAsoWiGeoAccess: draft?.includeAsoWiGeoAccess !== false,
        asoWiOtherGeographies: String(draft?.asoWiOtherGeographies || "").trim(),
        asoWiSlice: solution === "wi-slice",
        asoWiFullReplacement: solution === "wi-full-replacement",
        asoWiBoth: solution === "wi-both",
        asoWiShowBroadNetwork,
        asoWiShowCigna: asoWiShowBroadNetwork && includeAsoWiCigna,
        ...terms,
        showCignaCareManagementNote: templateType === "aso" && variant !== "partnership-only",
        hasStopLoss: Boolean(stopLoss),
        stopLossHtml: stopLoss,
        tables: asoTables(draft),
        footerPages: footerPages({ ...draft, templateType }),
        assets: {
            logo: svgUri(logoSvg),
            cMark: svgUri(cMarkSvg),
            bhcgLogo: absoluteAsset(bhcgLogoUrl),
        },
        pageBreak: '<div class="tpl-page-break-marker" data-page-break="true"></div>',
    });
}
